import React from 'react';

import type { StrictRJSFSchema } from '@rjsf/utils';

import { Alert, Box, Paper, Stack, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { ACCOUNT_STATUS } from '~/consts/accountTypes';

import useCurrentUser from '~/hooks/useCurrentUser';

import type { DisallowedRole } from '~/utils/accountRoles';
import { canManageAccountSecurity, isAdmin } from '~/utils/adminPermission';

import AccountStatusField from './AccountStatusField';
import AccountTypeField from './AccountTypeField';
import CustomClaimsField from './CustomClaimsField';
import ExternalIdentitiesField from './ExternalIdentitiesField';
import RolesAndVendorsField from './RolesAndVendorsField';
import { SECURITY_PANEL_HEADER_SX } from './utils';

type Props = {
  user: App.User;
  update: (user: App.User) => void;
  rolesSchema?: StrictRJSFSchema;
  disallowedRoles?: Array<DisallowedRole>;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
};

export default function SecurityTab({ user, update, rolesSchema, disallowedRoles, onUnsavedChangesChange }: Props) {
  const { user: currentUser } = useCurrentUser();
  // Account type and status check the same permission in their own fields.
  const isReadOnly = !canManageAccountSecurity(currentUser);
  // svc-auth only lets admins list identities, so these panels are not rendered (or loaded) for anyone else.
  const canViewAccountSecurity = isAdmin(currentUser);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h2" component="h2">
          Security
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Account settings, roles, external identities and custom claims.
        </Typography>
      </Box>
      {isReadOnly && (
        <Alert severity="info">
          {canViewAccountSecurity ? 'Account settings, external identities and custom claims' : 'Account settings'} can
          only be changed by users with both the admin-user and role-admin roles.
        </Alert>
      )}
      <Paper component="section" variant="outlined" sx={{ p: 3 }}>
        <PageSubheader title="Settings" showDivider={false} sx={SECURITY_PANEL_HEADER_SX} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <AccountTypeField
            accountId={user.id_member}
            accountType={user.account_type}
            onAccountTypeUpdated={(accountType) => update({ ...user, account_type: accountType })}
          />
          <AccountStatusField
            accountId={user.id_member}
            accountEnabled={user.status === ACCOUNT_STATUS.ENABLED}
            onAccountStatusUpdated={({ status, roles, vendors }) =>
              update({ ...user, status, roles: roles ?? user.roles, vendors: vendors ?? user.vendors })
            }
          />
        </Stack>
      </Paper>
      <RolesAndVendorsField
        accountId={user.id_member}
        accountType={user.account_type}
        accountEnabled={user.status === ACCOUNT_STATUS.ENABLED}
        roles={user.roles}
        vendors={user.vendors}
        rolesSchema={rolesSchema}
        disallowedRoles={disallowedRoles}
        onRolesAndVendorsUpdated={({ roles, vendors }) => update({ ...user, roles, vendors })}
        onUnsavedChangesChange={onUnsavedChangesChange}
      />
      {canViewAccountSecurity && (
        <>
          <ExternalIdentitiesField accountId={user.id_member} disabled={isReadOnly} />
          <CustomClaimsField
            accountId={user.id_member}
            disabled={isReadOnly}
            customClaims={user.custom_claims}
            onCustomClaimsUpdated={(customClaims) => update({ ...user, custom_claims: customClaims })}
          />
        </>
      )}
    </Stack>
  );
}
